import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';

export interface MetaResponse {
    success: boolean;
    message?: string;
    [key: string]: any;
}

export interface Partner {
    id: string;
    val: string;
    lbl: string;
    avatar: string;
    url_avatar: string;
    canProvider: boolean;
    canRelate: boolean;
    canRoute: boolean;
    canSponsor: boolean;
    defaultPartner: boolean;
    defaultTeamId: string;
    hasEcosystem: boolean;
    rootKbs: any;
}

export interface PartnerMetaResponse extends MetaResponse {
    pl__all_partners?: Partner[];
    pl__partners_users?: Array<{
        val: string;
        id: string;
        lbl: string;
        [key: string]: any;
    }>;
    pl__all_partners_teams?: Array<{
        val: string;
        id: string;
        lbl: string;
        partners_id: string;
        canRelate: boolean;
        canSponsor: boolean;
        canRoute: boolean;
        isSupportPartner: boolean;
        last_login: number | null;
        custom_email_domain: string;
        availability_periods: string;
        [key: string]: any;
    }>;
    pl__partners_teams?: Array<{
        val: string;
        id: string;
        lbl: string;
        partners_id: string;
        canRelate: boolean;
        canSponsor: boolean;
        canRoute: boolean;
        isSupportPartner: boolean;
        last_login: number | null;
        custom_email_domain: string;
        availability_periods: string;
        [key: string]: any;
    }>;
    pl__org_partners_teams?: Array<{
        val: string;
        id: string;
        lbl: string;
        partners_id: string;
        canRelate: boolean;
        canSponsor: boolean;
        canRoute: boolean;
        isSupportPartner: boolean;
        last_login: number | null;
        custom_email_domain: string;
        availability_periods: string;
        [key: string]: any;
    }>;
    current_server_time?: string;
}

export interface IssuePartnerTeamsResponse {
    success: boolean;
    message?: string;
    pl__issues_partners: Array<{
        val: string;
        lbl: string;
        members_locations_id: string;
        partners_id: string;
        id: string;
    }>;
    pl__issues_partners_teams: Array<{
        val: string;
        lbl: string;
        members_locations_id: string;
        partners_id: string;
        partners_teams_id: string;
        can_support: boolean;
        external: boolean;
        id: string;
    }>;
    current_server_time: string;
}

export interface MembersUsersResponse {
    success: boolean;
    message?: string;
    pl__members_users: Array<{
        val: string;
        id: string;
        members_id: string;
        email: string;
        lbl: string;
        public_name: string;
    }>;
    current_server_time: string;
}

export interface MemberProductsResponse {
    success: boolean;
    message?: string;
    pl__members_devices: Array<{
        val: string;
        id: string;
        nickname: string;
        members_id: string;
        members_locations_id: string;
        type: string;
        category: string;
        lbl: string;
    }>;
    current_server_time: string;
}

export interface FetchMembersUsersParams {
    members_id: string;
    members_locations_id: string;
    sponsor_id: string;
    context_org_id: string;
}

export interface FetchMemberProductsParams {
    members_id: string;
    members_locations_id: string;
}

export interface FetchMemberDevicesRollupParams {
    members_id: string;
    context_org_id: string;
    members_locations_id: string;
}

export interface MemberDevicesRollupResponse {
    success: boolean;
    message?: string;
    members_devices: Array<{
        id: string;
        val: string;
        lbl: string;
        nickname: string;
        members_id: string;
        members_locations_id: string;
        type: string;
        category: string;
        make: string;
        model: string;
        [key: string]: any;
    }>;
    totalCount: string;
    current_server_time: string;
}

export interface MembersLocationsPartnersResponse {
    success: boolean;
    message?: string;
    pl__members_partners_teams: Array<{
        val: string;
        lbl: string;
        members_locations_id: string;
        partners_id: string;
        partners_teams_id: string;
        can_support: boolean;
        external: boolean;
        id: string;
    }>;
    pl__members_partners: Array<{
        val: string;
        lbl: string;
        members_locations_id: string;
        partners_id: string;
        id: string;
    }>;
    current_server_time: string;
}

export interface FetchMembersLocationsPartnersParams {
    members_locations_id: string;
    orig_team_id?: string;
}

export interface PartnersTeamsUsersResponse {
    success: boolean;
    message?: string;
    pl__partners_teams_users: Array<{
        val: string;
        id: string;
        lbl: string;
        url_avatar: string;
        email: string;
        lbl_role: string;
        partners_id: string;
        avatar?: string;
    }>;
    current_server_time: string;
}

export interface FetchPartnersTeamsUsersParams {
    team_ids: string;
    search_all_partners?: boolean;
    long_labels?: boolean;
    page?: number;
    start?: number;
    limit?: number;
}

export interface CannedResponsesResponse {
    success: boolean;
    message?: string;
    canned_responses?: {
        totalCount: number;
        results: Array<{
            id: string;
            partners_id: string;
            users_id: string;
            partners_teams_ids: string[];
            name: string;
            content: string;
            data: any;
            status: number;
            updated: string;
            created: string;
            metadata: any;
            _title: string;
            c__owner_partner_name: string;
            c__owner_partner_nickname: string;
            c__d_status: string;
            c__creator_user_name: string;
            c__creator_nickname: string;
            c__teams_names: string[];
            c__data: string;
            _highlighted: boolean;
            _highlightmap: any;
            _has_detail: boolean;
            _canWrite: boolean;
            _uiAccess: {
                edit: boolean;
                delete: boolean;
                clone: boolean;
                merge: boolean;
            };
        }>;
    };
    // Legacy format for backward compatibility
    pl__canned_responses?: Array<{
        val: string;
        lbl: string;
        content: string;
        text: string;
        partners_id: string;
        partners_teams_ids: string[];
        users_id: string;
        upload_files: any;
        upload_files_names: any;
        id: string;
    }>;
    results?: Array<{
        val: string;
        lbl: string;
        content: string;
        text: string;
        partners_id: string;
        partners_teams_ids: string[];
        users_id: string;
        upload_files: any;
        upload_files_names: any;
        id: string;
    }>;
    current_server_time?: string;
    totalCount?: number;
}



export interface FetchCannedResponsesParams {
    object: string;
    object_id: string;
}

export interface KnowledgeBaseArticle {
    val: string;
    id: string;
    lbl: string;
    sub_title: string | null;
    short_name: string | null;
    root_parent_id: string;
    owner_partner_id: string;
    partner_ids: string[];
    internal_team_ids: string[];
    visibility: number;
    c__d_visibility: string;
    c__d_status: string;
    keywords: string | null;
    updated: string;
    url: string;
}

export interface KnowledgeBaseArticlesResponse {
    success: boolean;
    message?: string;
    pl__kbs: KnowledgeBaseArticle[];
    count: number;
    current_server_time: string;
}

export interface FetchKnowledgeBaseArticlesParams {
    current_kb_id?: string[];
    partner_ids?: string[];
    page?: number;
    start?: number;
    limit?: number;
    search?: string;
}

/**
 * Composable that provides access to Meta API functions.
 * This safely initializes the httpClient with authStore only when called 
 * inside components or other composables after Pinia is ready.
 */
export function useMetaAPI() {
    const authStore = useAuthStore();
    const userStore = useUserStore();
    const httpClient = getHttpClient(authStore);

    return {
        async fetchMetaData(): Promise<any> {
            try {
                const data = await httpClient.get<MetaResponse>('admin/v4/core/', {
                    sAction: 'meta'
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch metadata', data);
                throw new Error(data.message || 'Failed to fetch metadata');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching metadata:', error);
                throw error;
            }
        },

        async fetchPartnerMetaData(): Promise<PartnerMetaResponse> {
            try {            
                const data = await httpClient.get<PartnerMetaResponse>('admin/v4/core/', {
                    sAction: 'metaPartners'
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch partner metadata', data);
                throw new Error(data.message || 'Failed to fetch partner metadata');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching partner metadata:', error);
                throw error;
            }
        },

        async fetchPartnersTeams(): Promise<PartnerMetaResponse> {
            try {            
                const data = await httpClient.get<PartnerMetaResponse>('admin/v4/core/', {
                    sAction: 'metaPartnersTeams'
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch partners teams', data);
                throw new Error(data.message || 'Failed to fetch partners teams');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching partners teams:', error);
                throw error;
            }
        },

        async fetchIssuePartnerTeams(membersLocationId: string, issuesId: string): Promise<IssuePartnerTeamsResponse> {
            try {
                const filter = JSON.stringify([
                    { property: 'members_locations_id', value: membersLocationId },
                    { property: 'issues_id', value: issuesId }
                ]);
                
                const data = await httpClient.get<IssuePartnerTeamsResponse>('admin/v4/core/', {
                    sAction: 'metaIssuePartnerTeams',
                    page: '1',
                    start: '0',
                    limit: '25',
                    filter: filter
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch issue partner teams', data);
                throw new Error(data.message || 'Failed to fetch issue partner teams');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching issue partner teams:', error);
                throw error;
            }
        },

        async fetchMembersUsers(params: FetchMembersUsersParams): Promise<MembersUsersResponse> {
            try {
                const query = JSON.stringify([
                    { property: 'members_id', value: params.members_id },
                    { property: 'members_locations_id', value: params.members_locations_id },
                    { property: 'sponsor_id', value: params.sponsor_id },
                    { property: 'context_org_id', value: params.context_org_id }
                ]);
                
                const data = await httpClient.get<MembersUsersResponse>('admin/v4/core/', {
                    sAction: 'metaMembersUsers',
                    page: '1',
                    start: '0',
                    limit: '25',
                    query: query
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch members users', data);
                throw new Error(data.message || 'Failed to fetch members users');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching members users:', error);
                throw error;
            }
        },

        async fetchMemberProducts(params: FetchMemberProductsParams): Promise<MemberProductsResponse> {
            try {
                const filter = JSON.stringify([
                    { property: 'members_id', value: params.members_id },
                    { property: 'members_locations_id', value: params.members_locations_id }
                ]);
                
                const data = await httpClient.get<MemberProductsResponse>('admin/v4/core/', {
                    sAction: 'metaMembersDevices',
                    page: '1',
                    start: '0',
                    limit: '25',
                    filter: filter
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch member products', data);
                throw new Error(data.message || 'Failed to fetch member products');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching member products:', error);
                throw error;
            }
        },

        async fetchCannedResponses(params: FetchCannedResponsesParams): Promise<CannedResponsesResponse> {
            try {
                const filter = JSON.stringify([
                    { property: 'object', value: params.object },
                    { property: 'object_id', value: params.object_id }
                ]);
                
                const data = await httpClient.get<CannedResponsesResponse>('admin/v4/core/', {
                    sAction: 'metaCannedResponses',
                    page: '1',
                    start: '0',
                    limit: '50',
                    filter: filter
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch canned responses', data);
                throw new Error(data.message || 'Failed to fetch canned responses');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching canned responses:', error);
                throw error;
            }
        },

        async fetchMembersLocationsPartners(params: FetchMembersLocationsPartnersParams & { all_teams?: boolean }): Promise<MembersLocationsPartnersResponse> {
            try {
                const filterArray = [
                    { property: 'members_locations_id', value: params.members_locations_id }
                ];
                
                // Add all_teams filter if specified (convert boolean to string for API)
                if (params.all_teams !== undefined) {
                    filterArray.push({ property: 'all_teams', value: String(params.all_teams) });
                }
                
                // Add orig_team_id filter if provided
                if (params.orig_team_id) {
                    filterArray.push({ property: 'orig_team_id', value: params.orig_team_id });
                }
                
                const filter = JSON.stringify(filterArray);
                
                const data = await httpClient.get<MembersLocationsPartnersResponse>('admin/v4/core/', {
                    sAction: 'metaMembersLocationsPartners',
                    page: '1',
                    start: '0',
                    limit: '25',
                    filter: filter
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch members locations partners', data);
                throw new Error(data.message || 'Failed to fetch members locations partners');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching members locations partners:', error);
                throw error;
            }
        },

        async fetchPartnersTeamsUsers(params: FetchPartnersTeamsUsersParams): Promise<PartnersTeamsUsersResponse> {
            try {
                const filter = JSON.stringify([
                    { property: 'team_ids', value: [params.team_ids] }
                ]);
                
                const data = await httpClient.get<PartnersTeamsUsersResponse>('admin/v4/core/', {
                    sAction: 'metaPartnersTeamsUsers',
                    search_all_partners: String(params.search_all_partners ?? false),
                    long_labels: String(params.long_labels ?? true),
                    page: String(params.page ?? 1),
                    start: String(params.start ?? 0),
                    limit: String(params.limit ?? 25),
                    filter: filter
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch partners teams users', data);
                throw new Error(data.message || 'Failed to fetch partners teams users');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching partners teams users:', error);
                throw error;
            }
        },

        async fetchMemberDevicesRollup(params: FetchMemberDevicesRollupParams): Promise<MemberDevicesRollupResponse> {
            try {
                const filter = JSON.stringify([
                    { property: 'members_id', value: params.members_id },
                    { property: 'context_org_id', value: params.context_org_id },
                    { property: 'members_locations_id', value: params.members_locations_id }
                ]);

                const data = await httpClient.get<MemberDevicesRollupResponse>('admin/v4/members_devices/', {
                    sAction: 'listingProductRollup',
                    page: '1',
                    start: '0',
                    limit: '25',
                    filter: filter
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch member devices rollup', data);
                throw new Error(data.message || 'Failed to fetch member devices rollup');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching member devices rollup:', error);
                throw error;
            }
        },

        async fetchKnowledgeBaseArticles(params: FetchKnowledgeBaseArticlesParams = {}): Promise<KnowledgeBaseArticlesResponse> {
            // Build query filters
            const queryFilters = [];
            
            // Add current_kb_id filter (usually empty array for cases)
            queryFilters.push({
                value: params.current_kb_id || [],
                property: 'current_kb_id',
                operator: 'in_set'
            });
            
            // Add partner_ids filter
            if (params.partner_ids && params.partner_ids.length > 0) {
                queryFilters.push({
                    value: params.partner_ids,
                    property: 'partner_ids',
                    operator: 'intersect_set'
                });
            }
            
            // Add search query if provided (as a string at the end)
            if (params.search) {
                queryFilters.push(params.search);
            } else {
                // Add empty string at the end (matches API pattern)
                queryFilters.push('');
            }

            const queryParams: Record<string, string> = {
                sAction: 'metaKBs',
                page: String(params.page || 1),
                start: String(params.start || 0),
                limit: String(params.limit || 15),
                query: JSON.stringify(queryFilters)
            };

            try {
                const response = await httpClient.get<KnowledgeBaseArticlesResponse>('admin/v4/core/', queryParams);
                
                if (response.success) {
                    return response;
                }
                
                throw new Error(response.message || 'Failed to fetch knowledge base articles');
            } catch (error) {
                console.error('Error fetching knowledge base articles:', error);
                throw error;
            }
        },

        async fetchPartnersTeamsForUser(partnerId: string, userId: string): Promise<PartnerMetaResponse> {
            try {
                const filter = JSON.stringify([
                    { property: 'partners_id', value: partnerId },
                    { property: 'user_id', value: userId }
                ]);
                
                const data = await httpClient.get<PartnerMetaResponse>('admin/v4/core/', {
                    sAction: 'metaPartnersTeams2',
                    page: '1',
                    start: '0',
                    limit: '25',
                    filter: filter
                });

                if (data.success) {
                    return data;
                }
                
                console.error('❌ MetaAPI: Failed to fetch partners teams for user', data);
                throw new Error(data.message || 'Failed to fetch partners teams for user');
            } catch (error) {
                console.error('❌ MetaAPI: Error fetching partners teams for user:', error);
                throw error;
            }
        }
    };
} 